import { _toISOString } from "../../../helper/dateHelper";
import { fetchEmployeeSchedules, fetchEmployeeSchedulesBatch } from "../ScheduleFetcher";

// Day order for week cycle calculations
const DAY_ORDER = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const getDayIndex = (dayName) => DAY_ORDER.indexOf(dayName);

export function getScheduleForDay(employeeSchedule, date) {
  if (!employeeSchedule || !date ) {
    return null;
  }

  const scheduleArray = employeeSchedule.scheduleArray;
  if (!Array.isArray(scheduleArray)) {
    return null;
  }

  const sequence = employeeSchedule.sequence;
  const selectedOption = employeeSchedule.selectedOption;

  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });

  const matchesDayRange = (dayStr) => {
    if (!dayStr) return false;
    
    // Single day match
    if (!dayStr.includes(" - ")) {
      return weekday === dayStr;
    }
    
    // Day range match (e.g., "Friday - Tuesday")
    const [startDay, endDay] = dayStr.split(" - ").map(d => d.trim());
    const startIdx = getDayIndex(startDay);
    const endIdx = getDayIndex(endDay);
    const currentIdx = getDayIndex(weekday);
    
    // Invalid day names
    if (startIdx === -1 || endIdx === -1 || currentIdx === -1) {
      return false;
    }
    
    // Non-wrapping range (e.g., Monday - Friday)
    if (startIdx <= endIdx) {
      return currentIdx >= startIdx && currentIdx <= endIdx;
    }
    
    // Wrapping range (e.g., Friday - Tuesday: Fri, Sat, Sun, Mon, Tue)
    return currentIdx >= startIdx || currentIdx <= endIdx;
  };

  const getCycleDay = (cycleLength = 6) => {
    if (!employeeSchedule.startingDate) return null;
    const cycleBasedDate = new Date(employeeSchedule.startingDate);
    cycleBasedDate.setHours(0, 0, 0, 0);
    const queryDate = new Date(date);
    queryDate.setHours(0, 0, 0, 0);
    const dayDifference = Math.floor(
      (queryDate - cycleBasedDate) / (1000 * 60 * 60 * 24),
    );
    return (dayDifference % cycleLength) + 1;
  };

  const isDateAfterStarting = () => {
    if (!employeeSchedule.startingDate) return false;
    const cycleBasedDate = new Date(employeeSchedule.startingDate);
    return date >= cycleBasedDate;
  };

  switch (sequence) {
    case "10.5 HOURS":
      if (selectedOption === "4days Working, 2days off") {
        if (isDateAfterStarting()) {
          const cycleDay = getCycleDay();
          return cycleDay && cycleDay <= 4 ? scheduleArray[0] : null;
        }
        return null;
      } else {
        return scheduleArray.find((s) => s.day === weekday) || null;
      }

    case "9/12/12.5/13 HOURS":
      if (selectedOption === "Graveyard") {
        // console.log("scheduleArray", scheduleArray);
        for (const sched of scheduleArray) {
          if (matchesDayRange(sched.day)) {
            return sched;
          }
        }
        return null;
      } else if (selectedOption === "Regular 3days Working, 3days off") {
        if (isDateAfterStarting()) {
          const cycleDay = getCycleDay();
          return cycleDay && cycleDay <= 3 ? scheduleArray[0] : null;
        }
        return null;
      } else if (
        selectedOption === "Graveyard 3days Working, 3days off"
      ) {
        if (isDateAfterStarting()) {
          const cycleDay = getCycleDay();
          // 3 work / 3 off — final morning after night 3 is NOT a work day
          return cycleDay && cycleDay <= 3 ? scheduleArray[0] : null;
        }
        return null;
      } else if (selectedOption === "Regular 4days Working, 2days off") {
        if (isDateAfterStarting()) {
          const cycleDay = getCycleDay();
          return cycleDay && cycleDay <= 4 ? scheduleArray[0] : null;
        }
        return null;
      } else if (selectedOption === "Graveyard 4days Working, 2days off") {
        if (isDateAfterStarting()) {
          const cycleDay = getCycleDay(6);
          if (!cycleDay) {
            return null;
          }
          // 4 work / 2 off — morning after night 4 is NOT a 5th work day
          return cycleDay <= 4 ? scheduleArray[0] : null;
        }
        return null;
      } else {
        return scheduleArray.find((s) => s.day === weekday) || null;
      }

    case "24 HOURS":
      for (const sched of scheduleArray) {
        if (matchesDayRange(sched.day)) {
          return sched;
        }
      }
      return null;

    default:
      return scheduleArray.find((s) => s.day === weekday) || null;
  }
}

const workingDaysCache = new Map();

const createCacheKey = (employeeId, startStr, endStr) => {
  return `${employeeId || "no-employee"}_${startStr}_${endStr}`;
};

/**
 * Computes working days in the given date ranges for an employee.
 *
 * New behavior:
 * - Accepts employeeId instead of a single static schedule.
 * - Uses fetchEmployeeSchedules to load all schedule assignments up to the max date.
 * - For each date, picks the most recent assignment where createdDateStr <= dateStr.
 *
 * @param {string} employeeId
 * @param {Array} dateRanges - array of { dateRangeQuery: { $gte, $lte } }
 * @param {boolean|undefined} isArrayDate - same semantics as before:
 *   - undefined → return array of Sets
 *   - true      → return array of counts per range
 *   - false     → return total working days count
 * @param {Object} [scheduleCache] - optional cache object { byEmployee: Map }
 */
// export async function getWorkingDaysInRange(
//   employeeId,
//   dateRanges,
//   isArrayDate,
//   scheduleCache,
// ) {
//   if (!employeeId || !dateRanges?.length) {
//     return isArrayDate === undefined ? [] : isArrayDate ? [] : 0;
//   }

//   const cache = scheduleCache || { byEmployee: new Map() };

//   const endDates = dateRanges
//     .map((r) => r?.dateRangeQuery?.$lte)
//     .filter(Boolean)
//     .sort();

//   if (!endDates.length) {
//     return isArrayDate === undefined ? [] : isArrayDate ? [] : 0;
//   }

//   const maxDateStr = endDates[endDates.length - 1];

//   const employeeSchedules = cache?.byEmployee?.has(employeeId)
//     ? cache.byEmployee.get(employeeId)
//     : await fetchEmployeeSchedules(employeeId, maxDateStr, cache);

//   return calcWorkingDaysInRange(
//     employeeId,
//     dateRanges,
//     isArrayDate,
//     employeeSchedules,
//   );
// }

function calcWorkingDaysInRange(
  employeeId,
  dateRanges,
  isArrayDate,
  employeeSchedules,
) {
  const result = [];

  const getEffectiveScheduleForDateLocal = (dateStr) => {
    if (!employeeSchedules?.length) return null;
    for (const sched of employeeSchedules) {
      if (!sched.createdDateStr) continue;
      if (sched.createdDateStr <= dateStr) {
        return sched;
      }
    }
    return null;
  };

  for (const range of dateRanges) {
    const rangeQuery = range?.taggingDateRangeQuery ?? range?.dateRangeQuery;
    if (!rangeQuery) {
      result.push(new Set());
      continue;
    }

    const { $gte: startStr, $lte: endStr } = rangeQuery;
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);

    if (!startDate || !endDate) {
      result.push(new Set());
      continue;
    }

    // Determine if the NEWEST schedule assignment (index 0) is graveyard
    // const newestSchedule = employeeSchedules?.[0] || null;
    // const isGraveyardSchedule =
    //   newestSchedule != null &&
    //   (
    //     newestSchedule.selectedOption?.includes("Graveyard") ||
    //     newestSchedule.scheduleArray?.some(
    //       (d) =>
    //         d?.day?.includes(" - ") || d?.day?.includes("Graveyard"),
    //     )
    //   );

    // For regular (non-graveyard) employees: if endDate is the 1st of a month,
    // it likely means the date range parser added +1 day (e.g., Dec 31 -> Jan 1),
    // so subtract 1 day to get the actual end date (Dec 31).
    // For graveyard: keep endDate as is (they need the +1 day for shifts spanning two calendar days).
    if (endDate.getDate() === 1) {

      endDate.setDate(endDate.getDate() - 1);
    }

    const cacheKey = createCacheKey(employeeId, startStr, endStr);
    if (workingDaysCache.has(cacheKey)) {
      result.push(new Set(workingDaysCache.get(cacheKey)));
      continue;
    }

    const workingDaysSet = new Set();
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = _toISOString(currentDate);

      const effectiveSchedule = getEffectiveScheduleForDateLocal(dateStr);
      if (effectiveSchedule) {
        const scheduleForDay = getScheduleForDay(effectiveSchedule, currentDate);
        if (scheduleForDay) {
          workingDaysSet.add(dateStr);
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    workingDaysCache.set(cacheKey, Array.from(workingDaysSet));
    result.push(workingDaysSet);
  }

  if (isArrayDate === true) {
    return result.map((set) => set.size);
  } else if (isArrayDate === false) {
    return result.reduce((total, set) => total + set.size, 0);
  }

  return result;
}

export async function getWorkingDaysInRange(
  employeeIds,
  dateRanges,
  isArrayDate,
  batchSize = 200,
) {
  const cache = { byEmployee: new Map() };

  if (!employeeIds.length || !dateRanges?.length) {
    return new Map();
  }

  const endDates = dateRanges.map((r) => r?.dateRangeQuery?.$lte).filter(Boolean).sort();
  console.log("endDates", endDates);

  if (!endDates.length) return new Map();

  const maxDateStr = endDates[endDates.length - 1];
  console.log("maxDateStr", maxDateStr);
  await fetchEmployeeSchedulesBatch(employeeIds, maxDateStr, cache, batchSize );

  const resultMap = new Map();
  for (const empId of employeeIds) {
    const employeeSchedules = cache.byEmployee.get(empId) || [];
    const workingDaysSetArray = calcWorkingDaysInRange(
      empId,
      dateRanges,
      undefined,
      employeeSchedules,
    );

    const workingDaysCount =
      isArrayDate === true
        ? workingDaysSetArray.map((set) => set.size)
        : isArrayDate === false
          ? workingDaysSetArray.reduce((total, set) => total + set.size, 0)
          : 0;

    resultMap.set(empId, { workingDaysSetArray, workingDaysCount });
  }

  return resultMap;
}
